import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useState, useEffect } from 'react';
import { HeaderBackTitle } from '@/components/common/header';
import { _postURL } from "@/api/index";
import { CommonCategory } from '@/components/common/category';
import { TextInput, Slider, LoadingOverlay } from '@mantine/core';
import { ExpendMore } from '@/components/common/icon';
import { _numToPrice, _modelName, _getAllVehicleBrandList, _getAllVehicleModelList, _getAllVehicleVariant } from '../../libraries/common';
import { CenterModal, VehicleBrandModal, VehicleModelModal, VehicleVariantModal } from '@/components/modal';
import Redux, { updatePath, resetPath } from "@/action";
import { calculatorDefaultValue, pageLink, pageLinkSameUrl } from '../../config/site';
import _ from 'lodash';
import { BottomNavigation } from '../../components/common/navigation';
import { Footer } from '../../components/common/footer';
import { AllMeta } from '@/components/common/meta';
import Head from "next/head";
import { useRouter } from 'next/router';
import { MyBody } from '../../components/body/body';

const roadTax = {
    peninsularMalaysia: {
        saloonCarIndividual: {
            1000: {
                baseRate: 20,
                increaseRate: 0
            },
            1200: {
                baseRate: 55,
                increaseRate: 0
            },
            1400: {
                baseRate: 70,
                increaseRate: 0
            },
            1600: {
                baseRate: 90,
                increaseRate: 0
            },
            1800: {
                baseRate: 200,
                increaseRate: 0.4
            },
            2000: {
                baseRate: 280,
                increaseRate: 0.5
            },
            2500: {
                baseRate: 380,
                increaseRate: 1
            },
            3000: {
                baseRate: 880,
                increaseRate: 2.5
            },
            3001: {
                baseRate: 2130,
                increaseRate: 4.5
            },
        },
        saloonCarCompany: {
            1000: {
                baseRate: 20,
                increaseRate: 0
            },
            1200: {
                baseRate: 110,
                increaseRate: 0
            },
            1400: {
                baseRate: 140,
                increaseRate: 0
            },
            1600: {
                baseRate: 180,
                increaseRate: 0
            },
            1800: {
                baseRate: 400,
                increaseRate: 0.8
            },
            2000: {
                baseRate: 560,
                increaseRate: 1
            },
            2500: {
                baseRate: 760,
                increaseRate: 3
            },
            3000: {
                baseRate: 2260,
                increaseRate: 7.5
            },
            3001: {
                baseRate: 6010,
                increaseRate: 13.5
            },
        },
        nonSaloonCarIndividualCompany: {
            1000: {
                baseRate: 20,
                increaseRate: 0
            },
            1200: {
                baseRate: 85,
                increaseRate: 0
            },
            1400: {
                baseRate: 100,
                increaseRate: 0
            },
            1600: {
                baseRate: 120,
                increaseRate: 0
            },
            1800: {
                baseRate: 300,
                increaseRate: 0.3
            },
            2000: {
                baseRate: 360,
                increaseRate: 0.4
            },
            2500: {
                baseRate: 440,
                increaseRate: 0.8
            },
            3000: {
                baseRate: 840,
                increaseRate: 1.6
            },
            3001: {
                baseRate: 1640,
                increaseRate: 1.6
            },
        }
    },
    sabahSarawak: {
        saloonCarIndividual: {
            1000: {
                baseRate: 20,
                increaseRate: 0
            },
            1200: {
                baseRate: 44,
                increaseRate: 0
            },
            1400: {
                baseRate: 56,
                increaseRate: 0
            },
            1600: {
                baseRate: 72,
                increaseRate: 0
            },
            1800: {
                baseRate: 160,
                increaseRate: 0.32
            },
            2000: {
                baseRate: 224,
                increaseRate: 0.25
            },
            2500: {
                baseRate: 524,
                increaseRate: 1
            },
            3000: {
                baseRate: 880,
                increaseRate: 2.5
            },
            3001: {
                baseRate: 1024,
                increaseRate: 1.35
            },
        },
        saloonCarCompany: {
            1000: {
                baseRate: 20,
                increaseRate: 0
            },
            1200: {
                baseRate: 44,
                increaseRate: 0
            },
            1400: {
                baseRate: 56,
                increaseRate: 0
            },
            1600: {
                baseRate: 72,
                increaseRate: 0
            },
            1800: {
                baseRate: 160,
                increaseRate: 0.32
            },
            2000: {
                baseRate: 224,
                increaseRate: 0.25
            },
            2500: {
                baseRate: 524,
                increaseRate: 1
            },
            3000: {
                baseRate: 880,
                increaseRate: 2.5
            },
            3001: {
                baseRate: 1024,
                increaseRate: 1.35
            },
        },
        nonSaloonCarIndividualCompany: {
            1000: {
                baseRate: 20,
                increaseRate: 0
            },
            1200: {
                baseRate: 42.5,
                increaseRate: 0
            },
            1400: {
                baseRate: 50,
                increaseRate: 0
            },
            1600: {
                baseRate: 60,
                increaseRate: 0
            },
            1800: {
                baseRate: 165,
                increaseRate: 0.17
            },
            2000: {
                baseRate: 199,
                increaseRate: 0.22
            },
            2500: {
                baseRate: 243,
                increaseRate: 0.44
            },
            3000: {
                baseRate: 463,
                increaseRate: 0.88
            },
            3001: {
                baseRate: 903,
                increaseRate: 1.2
            },
        }
    }
}

const locationOption = [
    {
        label: "Peninsular Malaysia",
        value: "peninsularMalaysia"
    },
    {
        label: "Sabah / Sarawak",
        value: "sabahSarawak"
    }
]

const covergeType = [
    {
        label: "Saloon Cars / Individual Owned",
        value: "saloonCarIndividual"
    },
    {
        label: "Saloon Cars / Company Owned",
        value: "saloonCarCompany"
    },
    {
        label: "Non-Saloon Cars / Individual Or Company Owned",
        value: "nonSaloonCarIndividualCompany"
    },
]

const CarLoanCalculator = (props) => {
    const { t, valueField, handleChange, monthlyCost, downPaymentCost, totalCost, selectedVehicle, setVehicleBrandModalOpen, loading = false } = props

    const [downPaymentVal, setDownPaymentVal] = useState(valueField?.downPaymentVal)
    const [interestRateVal, setInterestRateVal] = useState(valueField?.interestRateVal)
    const [loanPeriodVal, setLoanPeriodVal] = useState(valueField?.loanPeriodVal)
    
    return (
        <div className='calculator_background_red'>
            <LoadingOverlay visible={loading} overlayBlur={2} zIndex={9} />
            <div className='calculator_header_title'>
                <p className='fontSize_28 fontWeight_bold fontColor_white'>
                    {`RM ${monthlyCost > 0 ? parseInt(monthlyCost).toLocaleString('en-US') : 0}`}
                </p>
                <p className='fontSize_14 fontColor_white'>
                    /month
                </p>
            </div>
            <div className='calculator_header_title_secondRow'>
                <div className='calculator_header_title2'>
                    <p className='fontSize_12 fontColor_red3'>
                        {t('down_payment')}
                    </p>
                    <p className='fontSize_16 fontWeight_semiBold fontColor_white'>
                        {`RM ${downPaymentCost > 0 ? parseInt(downPaymentCost).toLocaleString('en-US') : 0}`}
                    </p>
                </div>
                <div className='calculator_header_vertical_line' />
                <div className='calculator_header_title3'>
                    <p className='fontSize_12 fontColor_red3'>
                        {t('total_cost')}
                    </p>
                    <p className='fontSize_16 fontWeight_semiBold fontColor_white'>
                        {`RM ${totalCost > 0 ? parseInt(totalCost).toLocaleString('en-US') : 0}`}
                    </p>
                </div>
            </div>
            <div className='calculator_background_pink'>
                <div className='calculator_inputContainer'>
                    <p className='fontSize_14 fontWeight_bold'>
                        {t('select_car')}
                    </p>
                    <div onClick={()=>{setVehicleBrandModalOpen(true)}} className='calculator_inputBox cursorPointer'>
                        <p className={`${!_.isEmpty(selectedVehicle) ? 'fontSize_16 fontColor_red fontWeight_medium' : "fontSize_14 fontColor_red5"} whiteSpaceNowrap`}>
                            {
                                !_.isEmpty(selectedVehicle) ? selectedVehicle?.name : t('car')
                            }
                        </p>
                        <div className='calculator_inputRow_expendIcon'>
                            <ExpendMore color={'#FF8686'} />
                        </div>
                    </div>
                </div>
                <TextInput 
                    placeholder=''
                    label={t('car_price')}
                    value={`RM ${valueField?.vehiclePrice > 0 ? parseInt(valueField?.vehiclePrice).toLocaleString('en-US') : 0}`}
                    classNames={{
                        root: "calculator_textInputContainer",
                        label: "fontSize_14 fontWeight_bold",
                        input: "fontSize_16 fontWeight_medium calculator_textInput"
                    }}
                    onChange={(event)=>{handleChange(event.currentTarget.value, "vehiclePrice")}}
                />
                <div className='calculator_slider'>
                    <div className='calculator_slider_titleRow'>
                        <p className='fontSize_14 fontWeight_bold'>
                            {t('down_payment')}
                        </p>
                        <div className='calculator_slider_valueBox'>
                            <p className='fontSize_14 fontColor_red'>
                                {`${downPaymentVal}%`}
                            </p>
                        </div>
                    </div>
                    <div className='calculator_slider_sliderRow'>
                        <Slider
                            defaultValue={downPaymentVal}
                            min={1}
                            max={100}
                            step={1}
                            label={null}
                            classNames={{
                                track: "mantineCustome-Slider-track",
                                markWrapper: "mantineCustome-Slider-markWrapper",
                                mark: "mantineCustome-Slider-mark",
                                bar: "mantineCustome-Slider-bar",
                                thumb: "mantineCustome-Slider-thumb",
                                label: "fontSize_14 mantineCustome-Slider-label",
                                markLabel: "fontSize_14 calculator_slider_markLabel"
                            }}
                            marks={[
                                { value: 1, label: '1%' },
                                { value: 100, label: '100%' },
                            ]}
                            onChange={(value)=>{setDownPaymentVal(value)}}
                            onChangeEnd={(value)=>{handleChange(value, "downPayment")}}
                        />
                    </div>
                </div>
                <div className='calculator_slider'>
                    <div className='calculator_slider_titleRow'>
                        <p className='fontSize_14 fontWeight_bold'>
                            {t('interest_rate')}
                        </p>
                        <div className='calculator_slider_valueBox'>
                            <p className='fontSize_14 fontColor_red'>
                                {`${interestRateVal}%`}
                            </p>
                        </div>
                    </div>
                    <div className='calculator_slider_sliderRow'>
                        <Slider
                            defaultValue={interestRateVal}
                            min={0}
                            max={10}
                            step={0.1}
                            label={null}
                            classNames={{
                                track: "mantineCustome-Slider-track",
                                markWrapper: "mantineCustome-Slider-markWrapper",
                                mark: "mantineCustome-Slider-mark",
                                bar: "mantineCustome-Slider-bar",
                                thumb: "mantineCustome-Slider-thumb",
                                label: "fontSize_14 mantineCustome-Slider-label",
                                markLabel: "fontSize_14 calculator_slider_markLabel"
                            }}
                            marks={[
                                { value: 0, label: '0%' },
                                { value: 10, label: '10%' },
                            ]}
                            onChange={(value)=>{setInterestRateVal(value.toFixed(1))}}
                            onChangeEnd={(value)=>{handleChange(value, "interestRate")}}
                        />
                    </div>
                </div>
                <div className='calculator_slider'>
                    <div className='calculator_slider_titleRow'>
                        <p className='fontSize_14 fontWeight_bold'>
                            {t('loan_period_years')}
                        </p>
                        <div className='calculator_slider_valueBox'>
                            <p className='fontSize_14 fontColor_red'>
                                {loanPeriodVal}
                            </p>
                        </div>
                    </div>
                    <div className='calculator_slider_sliderRow'>
                        <Slider
                            defaultValue={loanPeriodVal}
                            min={1}
                            max={10}
                            step={1}
                            label={null}
                            classNames={{
                                track: "mantineCustome-Slider-track",
                                markWrapper: "mantineCustome-Slider-markWrapper",
                                mark: "mantineCustome-Slider-mark",
                                bar: "mantineCustome-Slider-bar",
                                thumb: "mantineCustome-Slider-thumb",
                                label: "fontSize_14 mantineCustome-Slider-label",
                                markLabel: "fontSize_14 calculator_slider_markLabel"
                            }}
                            marks={[
                                { value: 1, label: '1' },
                                { value: 10, label: '10' },
                            ]}
                            onChange={(value)=>{setLoanPeriodVal(value)}}
                            onChangeEnd={(value)=>{handleChange(value, "loanPeriod")}}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

const RoadTaxCalculator = (props) => {
    const { t, valueRoadTaxField, roadTaxLocation = {}, roadTaxCoverage = {}, handleChange, roadTaxYearly, selectedVehicle, setVehicleBrandModalOpen, setOpenLocationModal, setOpenCoverageModal, loading = false } = props
    
    return (
        <div className='calculator_background_red'>
            <LoadingOverlay visible={loading} overlayBlur={2} zIndex={9} />
            <div className='calculator_header_titleRoadTax'>
                <p className='fontSize_28 fontWeight_bold fontColor_white'>
                    {`${roadTaxYearly > 0 ? new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(roadTaxYearly) : 0}`}
                </p>
                <p className='fontSize_14 fontColor_white'>
                    {t('yearly_repayment')}
                </p>
            </div>
            <div className='calculator_background_pink'>
                <div className='calculator_inputContainer'>
                    <p className='fontSize_14 fontWeight_bold'>
                        {t('select_car')}
                    </p>
                    <div onClick={()=>{setVehicleBrandModalOpen(true)}} className='calculator_inputBox cursorPointer'>
                        <p className={`${!_.isEmpty(selectedVehicle) ? 'fontSize_16 fontColor_red fontWeight_medium' : "fontSize_14 fontColor_red5"} whiteSpaceNowrap`}>
                            {
                                !_.isEmpty(selectedVehicle) ? selectedVehicle?.name : t('car')
                            }
                        </p>
                        <div className='calculator_inputRow_expendIcon'>
                            <ExpendMore color={'#FF8686'} />
                        </div>
                    </div>
                </div>
                <TextInput 
                    placeholder=''
                    label={t("engine_capacity")}
                    value={valueRoadTaxField?.engineCapacity}
                    type='number'
                    classNames={{
                        root: "calculator_textInputContainer",
                        label: "fontSize_14 fontWeight_bold",
                        input: "fontSize_16 fontWeight_medium calculator_textInput"
                    }}
                    onChange={(event)=>{handleChange(event.currentTarget.value, "cc")}}
                />
                <div className='calculator_inputContainer calculator_formRowMargin'>
                    <p className='fontSize_14 fontWeight_bold'>
                        {t('location')}
                    </p>
                    <div onClick={()=>{setOpenLocationModal(true)}} className='calculator_inputBox cursorPointer'>
                        <p className={`${!_.isEmpty(roadTaxLocation) ? 'fontSize_16 fontColor_red fontWeight_medium' : "fontSize_14 fontColor_red5"} whiteSpaceNowrap`}>
                            {
                                !_.isEmpty(roadTaxLocation) ? roadTaxLocation?.label : "Select Location"
                            }
                        </p>
                        <div className='calculator_inputRow_expendIcon'>
                            <ExpendMore color={'#FF8686'} />
                        </div>
                    </div>
                </div>
                <div className='calculator_inputContainer calculator_formRowMargin'>
                    <p className='fontSize_14 fontWeight_bold'>
                        {t('coverage_type')}
                    </p>
                    <div onClick={()=>{setOpenCoverageModal(true)}} className='calculator_inputBox cursorPointer'>
                        <p className={`${!_.isEmpty(roadTaxCoverage) ? 'fontSize_16 fontColor_red fontWeight_medium' : "fontSize_14 fontColor_red5"} whiteSpaceNowrap`}>
                            {
                                !_.isEmpty(roadTaxCoverage) ? roadTaxCoverage?.label : "Select Coverage"
                            }
                        </p>
                        <div className='calculator_inputRow_expendIcon'>
                            <ExpendMore color={'#FF8686'} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default function Index(props) {
    const { t, device, session, params } = props;
    let defaultActiveCategory = 1
    if(params?.type?.[0] == "vehicle-insurance"){
        defaultActiveCategory = 2
    } else if(params?.type?.[0] == "vehicle-road-tax"){
        defaultActiveCategory = 3
    }

    const { store, dispatch } = Redux()
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState(defaultActiveCategory)
    const [selectedVehicle, setSelectedVehicle] = useState(store?.path?.calculatorCarLoad_object ?? null)

    // car load 
    const [monthlyCost, setMonthlyCost] = useState(0)
    const [downPaymentCost, setDownPaymentCost] = useState(0)
    const [totalCost, setTotalCost] = useState(0)
    const [valueField, setValueField] = useState({
        vehiclePrice: store?.path?.calculatorCarLoad_object?.price >= 0 ? store?.path?.calculatorCarLoad_object?.price : calculatorDefaultValue?.price, 
        downPaymentVal: calculatorDefaultValue?.downPaymentVal, 
        interestRateVal: calculatorDefaultValue?.interestRateVal, 
        loanPeriodVal: calculatorDefaultValue?.loanPeriodVal
    })

    // car road tax
    const [roadTaxYearly, setRoadTaxYearly] = useState(0)
    const [roadTaxLocation, setRoadTaxLocation] = useState(locationOption?.[0])
    const [roadTaxCoverage, setRoadTaxCoverage] = useState(covergeType?.[0])
    const [roadTaxEngineCapacity, setRoadTaxEngineCapacity] = useState(store?.path?.calculatorCarLoad_object?.capacity >= 0 ? (store?.path?.calculatorCarLoad_object?.capacity * 1000) : calculatorDefaultValue?.engineCapacity)
    const [valueRoadTaxField, setValueRoadTaxField] = useState({
        engineCapacity: store?.path?.calculatorCarLoad_object?.capacity >= 0 ? (store?.path?.calculatorCarLoad_object?.capacity * 1000) : calculatorDefaultValue?.engineCapacity, 
        location: locationOption?.[0]?.value,
        coverage: covergeType?.[0]?.value
    })

    // vehicle brand moal
    const [vehicleBrandModalOpen, setVehicleBrandModalOpen] = useState(false)
    const [vehicleBrandData, setVehicleBrandData] = useState([])
    const [vehicleBrandImage, setVehicleBrandImage] = useState(null)

    // vehicle model modal
    const [vehicleModelModalOpen, setVehicleModelModalOpen] = useState(false)
    const [vehicleModelData, setVehicleModelData] = useState([]) // model list by brand
    const [vehicleModelActive, setVehicleModelActive] = useState({})
    const [vehicleModelLoading, setVehicleModelLoading] = useState(false)

    // vehicle variant modal
    const [vehicleVariantModalOpen, setVehicleVariantModalOpen] = useState(false)
    const [vehicleVariantData, setVehicleVariantData] = useState({}) // variant list by model
    const [vehicleVariantLoading, setVehicleVaraintLoading] = useState(false)

    // location modal
    const [openLocationModal, setOpenLocationModal] = useState(false)
    const [openCoverageModal, setOpenCoverageModal] = useState(false)

    const router = useRouter()

    const calculationTap = [
        {id: 1, text: t("car_load")},
        // {id: 2, text: t("insurance"), isComingSoon: true}, TODOS
        {id: 3, text: t("road_tax")}
    ]

    const _handleClickBrandItem = async(value) => { 
        try {
            //  get all model list under vehicle brand id
            setVehicleModelLoading(true)
            setVehicleModelModalOpen(true)
            setVehicleBrandImage(value?.image_path)
            await _getAllVehicleModelList(setVehicleModelData, {
                vehicleBrandId: value?.vehicle_brand_id
            });
            setVehicleModelLoading(false)
        } catch(error) {
            console.log(error)
        }
    }

    const _handleClickModelItem = async(value) => {
        try {
            setVehicleVaraintLoading(true)
            setVehicleVariantModalOpen(true)
            setVehicleModelActive({image_path: value.image_path, brand_name: value.brand_name, model_name: _modelName(value.brand_name, value.name)})
            await _getAllVehicleVariant(setVehicleVariantData, {
                vehicle_model_id: value.vehicle_model_id
            })
            setVehicleVaraintLoading(false)
        } catch(error) {
            console.log(error)
        }
    }

    const _handleClickVariantItem = async(value) => {
        try {
            // selected vehicle
            setSelectedVehicle(value);
            // update vehicle price by choose vehicle
            setValueField((prevValue)=>{
                return {...prevValue, vehiclePrice: value?.price > 0 ? parseInt(value?.price) : 0}
            })
            // update road tax CC by chose vehicle
            setValueRoadTaxField((prevValue)=>{
                return {...prevValue, engineCapacity: parseFloat(value?.capacity) > 0 ? parseFloat(value?.capacity) * 1000 : 0 }
            })

            setVehicleBrandModalOpen(false)
            setVehicleModelModalOpen(false)
            setVehicleVariantModalOpen(false)
        } catch(error) {
            toast.error(error)
        }
    }

    // car load
    const _calcCarLoad = ({vehiclePrice, downPaymentVal, interestRateVal ,loanPeriodVal}) =>{
        let newDownPaymentCost = parseInt((parseInt(vehiclePrice) * (downPaymentVal * 0.01)).toFixed(0))
        let newLoadAmount = parseInt(vehiclePrice) - newDownPaymentCost
        let newTotalInterest = newLoadAmount * (interestRateVal * 0.01) * loanPeriodVal
        let newTotalCost = parseInt((parseInt(vehiclePrice) + newTotalInterest).toFixed(0))
        let newMonthlyCost = parseInt(((newLoadAmount + newTotalInterest) / (loanPeriodVal * 12)).toFixed(0))

        setMonthlyCost(newMonthlyCost)
        setDownPaymentCost(newDownPaymentCost)
        setTotalCost(newTotalCost)
    }

    const _handleChangeCarLoad = (value, type) => {
        if(type == "vehiclePrice"){
            let vehiclePriceChange =  parseInt(value.replace(/[^0-9]/g, ''), 10);
            
            setValueField((prevValue)=>{ 
                let newValue = {...prevValue, vehiclePrice: vehiclePriceChange}
                _calcCarLoad(newValue)
                return newValue
            })
        } else if(type == "downPayment"){

            setValueField((prevValue)=>{ 
                let newValue = {...prevValue, downPaymentVal: value}
                _calcCarLoad(newValue)
                return newValue
            })
        } else if (type == "interestRate"){

            setValueField((prevValue)=>{ 
                let newValue = {...prevValue, interestRateVal: value}
                _calcCarLoad(newValue)
                return newValue
            })
        } else if (type == "loanPeriod"){

            setValueField((prevValue)=>{ 
                let newValue = {...prevValue, loanPeriodVal: value}
                _calcCarLoad(newValue)
                return newValue
            })
        }
    }

    // road tax
    // https://www.kurnia.com/blog/road-tax-price-malaysia
    const _calcRoadTaxCalculation = (engineCapacity, roadTaxSelected, progressRateBaseCC = undefined) => {

        let baseRate = roadTaxSelected?.baseRate
        let progressRate = 0
        if(progressRateBaseCC > 0){
            progressRate = (engineCapacity - progressRateBaseCC) * roadTaxSelected?.increaseRate
        }
        let calculationRoadTaxResult = baseRate + progressRate
        setRoadTaxYearly(calculationRoadTaxResult)
    }

    const _calcRoadTax = ({engineCapacity, location, coverage}) => {
        if(engineCapacity >= 0, !_.isEmpty(location), !_.isEmpty(coverage)){
            if(engineCapacity <= 1000){
                // 1000 cc and below
                _calcRoadTaxCalculation(engineCapacity, roadTax?.[location]?.[coverage]?.[1000])
            } else if(engineCapacity <= 1200) {
                // 1001 cc ~ 1200 cc
                _calcRoadTaxCalculation(engineCapacity, roadTax?.[location]?.[coverage]?.[1200])
            } else if(engineCapacity <= 1400) {
                // 1201 cc ~ 1400 cc
                _calcRoadTaxCalculation(engineCapacity, roadTax?.[location]?.[coverage]?.[1400])
            } else if(engineCapacity <= 1600) {
                // 1401 cc ~ 1600 cc
                _calcRoadTaxCalculation(engineCapacity, roadTax?.[location]?.[coverage]?.[1600])
            } else if(engineCapacity <= 1800) {
                // 1601 cc ~ 1800 cc
                _calcRoadTaxCalculation(engineCapacity, roadTax?.[location]?.[coverage]?.[1800], 1600)
            } else if(engineCapacity <= 2000) {
                // 1801 cc ~ 2000 cc
                _calcRoadTaxCalculation(engineCapacity, roadTax?.[location]?.[coverage]?.[2000], 1800)
            } else if(engineCapacity <= 2500) {
                // 2001 cc ~ 2500 cc
                _calcRoadTaxCalculation(engineCapacity, roadTax?.[location]?.[coverage]?.[2500], 2000)
            } else if(engineCapacity <= 3000) {
                // 2501 cc ~ 3000 cc
                _calcRoadTaxCalculation(engineCapacity, roadTax?.[location]?.[coverage]?.[3000], 2500)
            } else {
                // 3001 cc ~ above
                _calcRoadTaxCalculation(engineCapacity, roadTax?.[location]?.[coverage]?.[3001], 3000)
            }
        }
    }

    const _handleChangeRoadTax = (value, type) => {
        if(type == "cc"){
            setValueRoadTaxField((prevValue)=>{
                let newValue = {...prevValue, engineCapacity: value}
                _calcRoadTax(newValue)
                return newValue
            })
        } else if(type == "location"){
            setRoadTaxLocation(value)
            setValueRoadTaxField((prevValue)=>{
                let newValue = {...prevValue, location: value?.value}
                _calcRoadTax(newValue)
                return newValue
            })
            setOpenLocationModal(false)
        } else if(type == "coverage"){
            setRoadTaxCoverage(value)
            setValueRoadTaxField((prevValue)=>{
                let newValue = {...prevValue, coverage: value?.value}
                _calcRoadTax(newValue)
                return newValue
            })
            setOpenCoverageModal(false)
        }
    }

    useEffect(()=>{
        //  get all brand list
        setLoading(true)
        const fetchData = async() => {
            if(_.isEmpty(store?.path?.dataAllVehicleBrandList)){
                await _getAllVehicleBrandList(dispatch);
            }
            if(!_.isEmpty(store?.path?.calculatorCarLoad_object)){
                //  empty 
                _calcCarLoad(valueField)
                _calcRoadTax(valueRoadTaxField)
                dispatch(updatePath({ key: "calculatorCarLoad_object", path: {} }));
            }
            setLoading(false)
        }
        fetchData();
    },[])

    // first time, and choose vehicle will load function re-calculation
    useEffect(()=>{
        _calcCarLoad(valueField)
        _calcRoadTax(valueRoadTaxField)
    },[selectedVehicle])

    const _handleTab = (type) => {
        setActiveCategory(type)

        // change page with refresh
        switch(type){
            case 1:
                router.push(`${pageLink?.calculator?.vehicleLoad}`, undefined, { shallow: true })
                break;
            case 2:
                break;
            case 3:
                router.push(`${pageLink?.calculator?.vehicleRoadTax}`, undefined, { shallow: true })
                break;
            default:
                break;
        }
        
    }

    // TODOS vehicle modal pop out got problem in here
    return (
        <>
            <MyBody
                t={t}
                isSubTab={true}
                headerComponent={
                    <HeaderBackTitle 
                        title={t("calculator")} 
                        showMenu={false}
                        t={t}
                        subTab={
                            <CommonCategory 
                                data={calculationTap}
                                activeCategory={activeCategory}
                                setActiveCategory={_handleTab}
                                t={t}
                                isAllAllow={false}
                                isFull={true}
                                isSubTab={true}
                            />
                        }
                    />
                }
                navigationComponent={
                    <BottomNavigation t={t} />
                }
                showNavigation={false}
                modalComponent={
                    <>
                        <VehicleBrandModal 
                            t={t}
                            vehicleBrandModalOpen={vehicleBrandModalOpen}
                            setVehicleBrandModalOpen={setVehicleBrandModalOpen}
                            handleClickBrandItem={_handleClickBrandItem}
                            vehicleBrandData={store?.path?.dataAllVehicleBrandList ?? []}
                            isMultipleModal={true}
                        />
                        <VehicleModelModal 
                            t={t}
                            vehicleModelModalOpen={vehicleModelModalOpen}
                            setVehicleModelModalOpen={setVehicleModelModalOpen}
                            handleClickModelItem={_handleClickModelItem}
                            vehicleModelData={vehicleModelData}
                            vehicleBrandImage={vehicleBrandImage}
                            isMultipleModal={true}
                            loading={vehicleModelLoading}
                        />
                        <VehicleVariantModal 
                            t={t}
                            vehicleVariantModalOpen={vehicleVariantModalOpen}
                            setVehicleVariantModalOpen={setVehicleVariantModalOpen}
                            handleClickVariantItem={_handleClickVariantItem}
                            vehicleVariantData={vehicleVariantData}
                            vehicleModelActive={vehicleModelActive}
                            isMultipleModal={true}
                            loading={vehicleVariantLoading}
                        />
                        <CenterModal
                            openModal={openLocationModal}
                            setOpenModal={setOpenLocationModal}
                        >
                            <div className='containerPadding'>
                                {
                                    _.reduce(locationOption, (result, value, key)=>{
                                        result.push(
                                            <div onClick={()=>{_handleChangeRoadTax(value, "location")}} key={key} className='center_modal_rowItem'>
                                                <p className='fontSize_14'>
                                                    {value.label}
                                                </p>
                                            </div>
                                        )
                                        return result
                                    },[])
                                }
                            </div>
                        </CenterModal>
                        <CenterModal
                            openModal={openCoverageModal}
                            setOpenModal={setOpenCoverageModal}
                        >
                            <div className='containerPadding'>
                                {
                                    _.reduce(covergeType, (result, value, key)=>{
                                        result.push(
                                            <div onClick={()=>{_handleChangeRoadTax(value, "coverage")}} key={key} className='center_modal_rowItem'>
                                                <p className='fontSize_14'>
                                                    {value.label}
                                                </p>
                                            </div>
                                        )
                                        return result
                                    },[])
                                }
                            </div>
                        </CenterModal>
                    </>
                }
            >
                {
                    store?.path?.isLoadedInFlutterApp == true &&
                    <div style={{paddingTop: '4rem'}} />
                }
                <div className='containerPadding calculator_container'>
                    {
                        activeCategory == 1 &&
                        <>
                            <AllMeta
                                title={t('calculatorVehicleLoanPage_meta_title')}
                                description={t('calculatorVehicleLoanPage_meta_description')}
                                keyword={t('calculatorVehicleLoanPage_meta_keyword')}
                                currentPageUrl={`${process.env.NEXT_PUBLIC_FRONTEND_URL}${pageLink.calculator.vehicleLoad}`}
                            />
                            <Head><title>{t('calculatorVehicleLoanPage_title')}</title></Head>
                            <CarLoanCalculator 
                                t={t} 
                                valueField={valueField}
                                handleChange={_handleChangeCarLoad}
                                monthlyCost={monthlyCost} 
                                downPaymentCost={downPaymentCost} 
                                totalCost={totalCost}
                                selectedVehicle={selectedVehicle}
                                setVehicleBrandModalOpen={setVehicleBrandModalOpen}
                                loading={loading}
                            />
                        </>
                    }
                    {
                        activeCategory == 2 &&
                        <></> // TODOS
                    }
                    {
                        activeCategory == 3 &&
                        <>
                            <AllMeta
                                title={t('calculatorVehicleRoadTaxPage_meta_title')}
                                description={t('calculatorVehicleRoadTaxPage_meta_description')}
                                keyword={t('calculatorVehicleRoadTaxPage_meta_keyword')}
                                currentPageUrl={`${process.env.NEXT_PUBLIC_FRONTEND_URL}${pageLink.calculator.vehicleRoadTax}`}
                            />
                            <Head><title>{t('calculatorVehicleRoadTaxPage_title')}</title></Head>
                            <RoadTaxCalculator 
                                t={t} 
                                valueRoadTaxField={valueRoadTaxField}
                                roadTaxLocation={roadTaxLocation}
                                roadTaxCoverage={roadTaxCoverage}
                                handleChange={_handleChangeRoadTax}
                                roadTaxYearly={roadTaxYearly} 
                                selectedVehicle={selectedVehicle}
                                setVehicleBrandModalOpen={setVehicleBrandModalOpen}
                                setOpenLocationModal={setOpenLocationModal}
                                setOpenCoverageModal={setOpenCoverageModal}
                                loading={loading}
                            />
                        </>
                    }
                </div>
            </MyBody>
        </>
    )
    
    // return (
    //     <div className='bodyContainer'>
    //         <div className='contentContainer bodyContainerHasBottomNavigation'>
    //             <HeaderBackTitle 
    //                 title={t("calculator")} 
    //                 showMenu={false}
    //                 t={t}
    //             />
    //             <div>
    //                 <CommonCategory 
    //                     data={calculationTap}
    //                     activeCategory={activeCategory}
    //                     setActiveCategory={_handleTab}
    //                     t={t}
    //                     isAllAllow={false}
    //                     isFull={true}
    //                 />
    //             </div>
    //             <div className='containerPadding calculator_container'>
    //                 {
    //                     activeCategory == 1 &&
    //                     <>
    //                         <AllMeta
    //                             title={t('calculatorVehicleLoanPage_meta_title')}
    //                             description={t('calculatorVehicleLoanPage_meta_description')}
    //                             keyword={t('calculatorVehicleLoanPage_meta_keyword')}
    //                             currentPageUrl={`${process.env.NEXT_PUBLIC_FRONTEND_URL}${pageLink.calculator.vehicleLoad}`}
    //                         />
    //                         <Head><title>{t('calculatorVehicleLoanPage_title')}</title></Head>
    //                         <CarLoanCalculator 
    //                             t={t} 
    //                             valueField={valueField}
    //                             handleChange={_handleChangeCarLoad}
    //                             monthlyCost={monthlyCost} 
    //                             downPaymentCost={downPaymentCost} 
    //                             totalCost={totalCost}
    //                             selectedVehicle={selectedVehicle}
    //                             setVehicleBrandModalOpen={setVehicleBrandModalOpen}
    //                             loading={loading}
    //                         />
    //                     </>
    //                 }
    //                 {
    //                     activeCategory == 2 &&
    //                     <>Coming Soon</>
    //                 }
    //                 {
    //                     activeCategory == 3 &&
    //                     <>
    //                         <AllMeta
    //                             title={t('calculatorVehicleRoadTaxPage_meta_title')}
    //                             description={t('calculatorVehicleRoadTaxPage_meta_description')}
    //                             keyword={t('calculatorVehicleRoadTaxPage_meta_keyword')}
    //                             currentPageUrl={`${process.env.NEXT_PUBLIC_FRONTEND_URL}${pageLink.calculator.vehicleRoadTax}`}
    //                         />
    //                         <Head><title>{t('calculatorVehicleRoadTaxPage_title')}</title></Head>
    //                         <RoadTaxCalculator 
    //                             t={t} 
    //                             valueRoadTaxField={valueRoadTaxField}
    //                             roadTaxLocation={roadTaxLocation}
    //                             roadTaxCoverage={roadTaxCoverage}
    //                             handleChange={_handleChangeRoadTax}
    //                             roadTaxYearly={roadTaxYearly} 
    //                             selectedVehicle={selectedVehicle}
    //                             setVehicleBrandModalOpen={setVehicleBrandModalOpen}
    //                             setOpenLocationModal={setOpenLocationModal}
    //                             setOpenCoverageModal={setOpenCoverageModal}
    //                             loading={loading}
    //                         />
    //                     </>
    //                 }
    //             </div>
    //             <Footer t={t} />
    //         </div>
    //         <BottomNavigation t={t} />
    //         <VehicleBrandModal 
    //             t={t}
    //             vehicleBrandModalOpen={vehicleBrandModalOpen}
    //             setVehicleBrandModalOpen={setVehicleBrandModalOpen}
    //             handleClickBrandItem={_handleClickBrandItem}
    //             vehicleBrandData={store?.path?.dataAllVehicleBrandList ?? []}
    //             isMultipleModal={true}
    //         />
    //         <VehicleModelModal 
    //             t={t}
    //             vehicleModelModalOpen={vehicleModelModalOpen}
    //             setVehicleModelModalOpen={setVehicleModelModalOpen}
    //             handleClickModelItem={_handleClickModelItem}
    //             vehicleModelData={vehicleModelData}
    //             vehicleBrandImage={vehicleBrandImage}
    //             isMultipleModal={true}
    //             loading={vehicleModelLoading}
    //         />
    //         <VehicleVariantModal 
    //             t={t}
    //             vehicleVariantModalOpen={vehicleVariantModalOpen}
    //             setVehicleVariantModalOpen={setVehicleVariantModalOpen}
    //             handleClickVariantItem={_handleClickVariantItem}
    //             vehicleVariantData={vehicleVariantData}
    //             vehicleModelActive={vehicleModelActive}
    //             isMultipleModal={true}
    //             loading={vehicleVariantLoading}
    //         />
    //         <CenterModal
    //             openModal={openLocationModal}
    //             setOpenModal={setOpenLocationModal}
    //         >
    //             <div className='containerPadding'>
    //                 {
    //                     _.reduce(locationOption, (result, value, key)=>{
    //                         result.push(
    //                             <div onClick={()=>{_handleChangeRoadTax(value, "location")}} key={key} className='center_modal_rowItem'>
    //                                 <p className='fontSize_14'>
    //                                     {value.label}
    //                                 </p>
    //                             </div>
    //                         )
    //                         return result
    //                     },[])
    //                 }
    //             </div>
    //         </CenterModal>
    //         <CenterModal
    //             openModal={openCoverageModal}
    //             setOpenModal={setOpenCoverageModal}
    //         >
    //             <div className='containerPadding'>
    //                 {
    //                     _.reduce(covergeType, (result, value, key)=>{
    //                         result.push(
    //                             <div onClick={()=>{_handleChangeRoadTax(value, "coverage")}} key={key} className='center_modal_rowItem'>
    //                                 <p className='fontSize_14'>
    //                                     {value.label}
    //                                 </p>
    //                             </div>
    //                         )
    //                         return result
    //                     },[])
    //                 }
    //             </div>
    //         </CenterModal>
    //     </div>
    // )
}

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking'
    };
}

export async function getStaticProps({ locale, params }) {
    try{
        if(!(params?.type == pageLinkSameUrl.calculator.vehicleLoad || params?.type == pageLinkSameUrl.calculator.vehicleInsurance || params?.type == pageLinkSameUrl.calculator.vehicleRoadTax)){
            throw new Error()
        }
        return {
            props: {
                ...(await serverSideTranslations(locale, ['common'])),
                params
            },
        };
    } catch(error) {
        return {
            notFound: true,
        }
    }
    
}